import { MoveToProjectModal } from '@/components/MoveToProjectModal';
import { RenameDialog } from '@/components/RenameDialog';
import { DataTable } from '@/components/table/DataTable';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { Input } from '@/pages/Ask/components/Input';
import { paths } from '@/paths';
import { useDeleteChatService, useUpdateChatService } from '@/services/chat';
import { useProjectService } from '@/services/projects';
import {
  useCreateTableViewService,
  useDeleteTableViewService,
  useUpdateTableViewService,
} from '@/services/tableViews';
import { WORKSPACE_PATH } from '@/paths';
import { useChatNavigate } from '@/utils/aiUtils';
import { ColumnDef } from '@tanstack/react-table';
import { getPaginationRowModel } from '@tanstack/react-table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  ArrowLeft,
  FolderMinus,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { FC, useState } from 'react';
import {
  generatePath,
  NavLink,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { EditProjectDialog } from '../components/EditProjectDialog';

dayjs.extend(relativeTime);

type ChatSummary = {
  id: string;
  title: string | null;
  createdAt: string;
};

type TableViewSummary = {
  id: string;
  name: string;
  createdAt: string;
};

// ─── Chat row actions ─────────────────────────────────────────────

const ChatRowActions: FC<{
  chat: ChatSummary;
  onNavigate: (id: string) => void;
}> = ({ chat }) => {
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { updateChat, isUpdatingChat } = useUpdateChatService();
  const { deleteChat } = useDeleteChatService(chat.id);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setIsRenameOpen(true)}>
            <Pencil className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setIsMoveOpen(true)}>
            <FolderPlus className="h-4 w-4" />
            Change project
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              updateChat(
                { chatId: chat.id, data: { projectId: null } },
                { onSuccess: () => toast.success('Chat removed from project') }
              )
            }
          >
            <FolderMinus className="h-4 w-4" />
            Remove from project
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RenameDialog
        open={isRenameOpen}
        onOpenChange={setIsRenameOpen}
        currentName={chat.title || 'Untitled chat'}
        onSave={newName =>
          updateChat(
            { chatId: chat.id, data: { title: newName } },
            { onSuccess: () => toast.success('Chat renamed') }
          )
        }
        isLoading={isUpdatingChat}
        title="Rename chat"
      />
      <MoveToProjectModal
        open={isMoveOpen}
        onOpenChange={setIsMoveOpen}
        onSelect={projectId =>
          updateChat(
            { chatId: chat.id, data: { projectId } },
            { onSuccess: () => toast.success('Chat moved to project') }
          )
        }
        isLoading={isUpdatingChat}
        title="Change project"
        subtitle="Select a new project for this chat"
      />
      {isDeleteOpen && (
        <DeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          title={`Delete "${chat.title || 'Untitled chat'}"?`}
          description="This action cannot be undone."
          onConfirm={() =>
            deleteChat(undefined, {
              onSuccess: () => toast.success('Chat deleted'),
            })
          }
        />
      )}
    </>
  );
};

// ─── Table view row actions ───────────────────────────────────────

const TableViewRowActions: FC<{
  tableView: TableViewSummary;
}> = ({ tableView }) => {
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { updateTableView, isUpdateTableViewPending } =
    useUpdateTableViewService();
  const { deleteTableView } = useDeleteTableViewService();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setIsRenameOpen(true)}>
            <Pencil className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setIsMoveOpen(true)}>
            <FolderPlus className="h-4 w-4" />
            Change project
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              updateTableView(
                {
                  tableViewId: tableView.id,
                  data: { projectId: null },
                },
                {
                  onSuccess: () =>
                    toast.success('Table view removed from project'),
                }
              )
            }
          >
            <FolderMinus className="h-4 w-4" />
            Remove from project
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RenameDialog
        open={isRenameOpen}
        onOpenChange={setIsRenameOpen}
        currentName={tableView.name}
        onSave={newName =>
          updateTableView(
            {
              tableViewId: tableView.id,
              data: { name: newName },
            },
            { onSuccess: () => toast.success('Table view renamed') }
          )
        }
        isLoading={isUpdateTableViewPending}
        title="Rename table view"
      />
      <MoveToProjectModal
        open={isMoveOpen}
        onOpenChange={setIsMoveOpen}
        onSelect={projectId =>
          updateTableView(
            {
              tableViewId: tableView.id,
              data: { projectId },
            },
            { onSuccess: () => toast.success('Table view moved to project') }
          )
        }
        isLoading={isUpdateTableViewPending}
        title="Change project"
        subtitle="Select a new project for this table view"
      />
      {isDeleteOpen && (
        <DeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          title={`Delete "${tableView.name}"?`}
          description="This action cannot be undone."
          onConfirm={() =>
            deleteTableView(tableView.id, {
              onSuccess: () => toast.success('Table view deleted'),
            })
          }
        />
      )}
    </>
  );
};

// ─── Shared delete confirm dialog ─────────────────────────────────

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const DeleteConfirmDialog: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => void;
}> = ({ open, onOpenChange, title, description, onConfirm }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main page component ──────────────────────────────────────────

export const ProjectPage: FC = () => {
  const { project_id } = useParams<{ project_id: string }>();
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const navigate = useNavigate();
  const { project, isPending } = useProjectService(project_id ?? '');
  const { navigateToChat } = useChatNavigate({ projectId: project_id });
  const { createTableView, isCreating: isCreatingTableView } =
    useCreateTableViewService();

  const [prompt, setPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('chats');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [chatPagination, setChatPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [tvPagination, setTvPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const handleSubmit = async (promptText: string) => {
    await navigateToChat({ initialMessage: { text: promptText } });
  };

  const handleChatRowClick = (chatId: string) => {
    const chatsPath = generatePath(`${WORKSPACE_PATH}/${paths.chats}`, {
      workspaceUname: workspaceUname ?? '',
    });
    navigate(`${chatsPath}/${chatId}`);
  };

  const handleTableViewRowClick = (tvId: string) => {
    const tvPath = generatePath(`${WORKSPACE_PATH}/${paths.tableViews}`, {
      workspaceUname: workspaceUname ?? '',
    });
    navigate(`${tvPath}/${tvId}`);
  };

  const handleCreateTableView = () => {
    createTableView(
      { name: 'New Table View', projectId: project_id },
      {
        onSuccess: data => {
          const tvPath = generatePath(`${WORKSPACE_PATH}/${paths.tableViews}`, {
            workspaceUname: workspaceUname ?? '',
          });
          navigate(`${tvPath}/${data.id}`, {
            state: { openTemplateDialog: true },
          });
        },
      }
    );
  };

  if (isPending) {
    return (
      <FlexCol p="6" w="full" gap="6" className="max-w-[1600px]">
        <Skeleton className="w-32 h-4" />
        <Skeleton className="w-64 h-8" />
        <Skeleton className="w-96 h-4" />
        <Skeleton className="w-full h-64" />
      </FlexCol>
    );
  }

  if (!project) {
    return (
      <FlexCol p="6" w="full" gap="6">
        <Text variant="body" color="primary">
          Project not found
        </Text>
      </FlexCol>
    );
  }

  const chatColumnsWithActions: ColumnDef<ChatSummary>[] = [
    {
      accessorKey: 'title',
      header: () => (
        <Text variant="body-sm" color="primary">
          Title
        </Text>
      ),
      cell: ({ row }) => (
        <FlexRow
          className="cursor-pointer hover:underline"
          onClick={() => handleChatRowClick(row.original.id)}
        >
          <Text variant="body-sm" color="primary">
            {row.original.title || 'Untitled chat'}
          </Text>
        </FlexRow>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <Text variant="body-sm" color="primary">
          Created
        </Text>
      ),
      cell: ({ row }) => (
        <Text variant="body-sm" color="secondary">
          {dayjs(row.original.createdAt).fromNow()}
        </Text>
      ),
    },
    {
      id: 'actions',
      meta: {
        headerClassName: 'w-10',
        cellClassName: 'w-10',
      },
      header: () => null,
      cell: ({ row }) => (
        <ChatRowActions chat={row.original} onNavigate={handleChatRowClick} />
      ),
    },
  ];

  const tvColumnsWithActions: ColumnDef<TableViewSummary>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <Text variant="body-sm" color="primary">
          Name
        </Text>
      ),
      cell: ({ row }) => (
        <FlexRow
          className="cursor-pointer hover:underline"
          onClick={() => handleTableViewRowClick(row.original.id)}
        >
          <Ellipsis className="max-w-[50ch] w-full" asChild hasTooltip>
            <Text variant="body-sm" color="primary">
              {row.original.name}
            </Text>
          </Ellipsis>
        </FlexRow>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <Text variant="body-sm" color="primary">
          Created
        </Text>
      ),
      cell: ({ row }) => (
        <Text variant="body-sm" color="secondary">
          {dayjs(row.original.createdAt).fromNow()}
        </Text>
      ),
    },
    {
      id: 'actions',
      meta: {
        headerClassName: 'w-10',
        cellClassName: 'w-10',
      },
      header: () => null,
      cell: ({ row }) => <TableViewRowActions tableView={row.original} />,
    },
  ];

  return (
    <FlexCol p="6" w="full" gap="6" className="max-w-[1600px]">
      {/* Back link */}
      <NavLink
        to=".."
        className="flex items-center gap-1 text-sm text-foreground hover:text-foreground w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        All Projects
      </NavLink>

      {/* Header */}
      <FlexCol gap="2">
        <FlexRow alignItems="center" gap="2">
          <Text variant="header1" color="primary">
            {project.name}
          </Text>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsEditOpen(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </FlexRow>
        {project.description && (
          <Text variant="body" color="secondary">
            {project.description}
          </Text>
        )}
      </FlexCol>

      {/* Chat input */}
      <FlexCol className="w-full max-w-[722px]">
        <Input
          onSubmit={handleSubmit}
          currentPrompt={prompt}
          setCurrentPrompt={setPrompt}
          rows={3}
        />
        <FlexRow w="full" className="border-b border-neutral-300 rounded-b-lg">
          <Box
            w="1/3"
            h="1"
            className="bg-yellow-400 -mr-[10px] mix-blend-multiply rounded-bl-lg"
          />
          <Box
            w="1/3"
            h="1"
            className="bg-green-300 -mr-[10px] mix-blend-multiply"
          />
          <Box
            w="1/3"
            h="1"
            className="bg-blue-500 -mr-[10px] mix-blend-multiply"
          />
          <Box
            w="1/3"
            h="1"
            className="bg-magenta-500 -mr-[10px] mix-blend-multiply"
          />
          <Box
            w="1/3"
            h="1"
            className="bg-yellow-400 mix-blend-multiply rounded-br-lg"
          />
        </FlexRow>
      </FlexCol>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <FlexRow alignItems="center" justifyContent="between" w="full">
          <TabsList variant="underline">
            <TabsTrigger value="chats" variant="underline">
              Chats
            </TabsTrigger>
            <TabsTrigger value="tableViews" variant="underline">
              Table Views
            </TabsTrigger>
          </TabsList>
          {activeTab === 'tableViews' && (
            <Button
              size="sm"
              onClick={handleCreateTableView}
              disabled={isCreatingTableView}
            >
              <Plus className="h-4 w-4" />
              New Table View
            </Button>
          )}
        </FlexRow>

        <TabsContent value="chats">
          {project.chats.length === 0 ? (
            <FlexCol
              p="8"
              alignItems="center"
              justifyContent="center"
              gap="2"
              className="border border-dashed border-neutral-200 rounded-lg"
            >
              <Text variant="body-sm" color="secondary">
                No chats yet. Start a chat to begin.
              </Text>
            </FlexCol>
          ) : (
            <DataTable
              columns={chatColumnsWithActions}
              data={project.chats}
              options={{
                getPaginationRowModel: getPaginationRowModel(),
                onPaginationChange: setChatPagination,
                getRowId: row => row.id,
                state: { pagination: chatPagination },
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="tableViews">
          {project.tableViews.length === 0 ? (
            <FlexCol
              p="8"
              alignItems="center"
              justifyContent="center"
              gap="2"
              className="border border-dashed border-neutral-200 rounded-lg"
            >
              <Text variant="body-sm" color="secondary">
                No table views linked to this project yet.
              </Text>
            </FlexCol>
          ) : (
            <DataTable
              columns={tvColumnsWithActions}
              data={project.tableViews}
              options={{
                getPaginationRowModel: getPaginationRowModel(),
                onPaginationChange: setTvPagination,
                getRowId: row => row.id,
                state: { pagination: tvPagination },
              }}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <EditProjectDialog
        projectId={project_id ?? ''}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
    </FlexCol>
  );
};
